import {useState} from "react";
import {useTranslation} from "react-i18next";
import {css, Theme} from "@emotion/react";
import {elevation} from "@pg-design/elevation";
import {
    backgroundColor,
    borderRadius,
    flex,
    flexJustifyCenter,
    h100,
    onDesktop,
    order,
    p
} from "@pg-design/helpers-css";
import {Select} from "@pg-design/select-v2";
import {Text} from "@pg-design/text";

import {flexGap} from "../../../components/css_helpers/flex";
import {SUBSCRIPTION_PERIODS} from "./BdSubscriptionBox";
import {Price} from "./Price";

type SUBSCRIPTION_VOIVODESHIPS =
    | "mazowieckie"
    | "dolnoslaskie"
    | "wielkopolskie"
    | "pomorskie"
    | "lodzkie"
    | "malopolskie"
    | "slaskie"
    | "zachodnio-pomorskie"
    | "warminsko-mazurskie"
    | "podlaskie"
    | "kujawsko-pomorskie"
    | "swietokrzyskie"
    | "lubelskie"
    | "podkarpackie"
    | "opolskie"
    | "lubuskie";

const SUBSCRIPTION_PRICES = {
    mazowieckie: {
        monthly: "950",
        annually: "799"
    },
    dolnoslaskie: {
        monthly: "720",
        annually: "599"
    },
    wielkopolskie: {
        monthly: "720",
        annually: "599"
    },
    pomorskie: {
        monthly: "720",
        annually: "599"
    },
    lodzkie: {
        monthly: "720",
        annually: "599"
    },
    malopolskie: {
        monthly: "720",
        annually: "599"
    },
    slaskie: {
        monthly: "720",
        annually: "599"
    },
    "zachodnio-pomorskie": {
        monthly: "360",
        annually: "299"
    },
    "warminsko-mazurskie": {
        monthly: "360",
        annually: "299"
    },
    podlaskie: {
        monthly: "360",
        annually: "299"
    },
    "kujawsko-pomorskie": {
        monthly: "360",
        annually: "299"
    },
    swietokrzyskie: {
        monthly: "360",
        annually: "299"
    },
    lubelskie: {
        monthly: "360",
        annually: "299"
    },
    podkarpackie: {
        monthly: "360",
        annually: "299"
    },
    opolskie: {
        monthly: "360",
        annually: "299"
    },
    lubuskie: {
        monthly: "360",
        annually: "299"
    }
};

interface ISelectedCityPricingBoxProps {
    paymentPeriod: SUBSCRIPTION_PERIODS;
}

export const SelectedCityPricingBox = ({paymentPeriod}: ISelectedCityPricingBoxProps) => {
    const {t} = useTranslation();
    const [selectedVoivodeship, setSelectedVoivodeship] = useState<SUBSCRIPTION_VOIVODESHIPS>("mazowieckie");

    const options: {value: SUBSCRIPTION_VOIVODESHIPS; label: string}[] = [
        {
            value: "mazowieckie",
            label: t("voivodeship.mazowieckie")
        },
        {
            value: "dolnoslaskie",
            label: t("voivodeship.dolnoslaskie")
        },
        {
            value: "wielkopolskie",
            label: t("voivodeship.wielkopolskie")
        },
        {
            value: "pomorskie",
            label: t("voivodeship.pomorskie")
        },
        {
            value: "lodzkie",
            label: t("voivodeship.lodzkie")
        },
        {
            value: "malopolskie",
            label: t("voivodeship.malopolskie")
        },
        {
            value: "slaskie",
            label: t("voivodeship.slaskie")
        },
        {
            value: "zachodnio-pomorskie",
            label: t("voivodeship.zachodnio-pomorskie")
        },
        {
            value: "warminsko-mazurskie",
            label: t("voivodeship.warminsko-mazurskie")
        },
        {
            value: "podlaskie",
            label: t("voivodeship.podlaskie")
        },
        {
            value: "kujawsko-pomorskie",
            label: t("voivodeship.kujawsko-pomorskie")
        },
        {
            value: "swietokrzyskie",
            label: t("voivodeship.swietokrzyskie")
        },
        {
            value: "lubelskie",
            label: t("voivodeship.lubelskie")
        },
        {
            value: "podkarpackie",
            label: t("voivodeship.podkarpackie")
        },
        {
            value: "opolskie",
            label: t("voivodeship.opolskie")
        },
        {
            value: "lubuskie",
            label: t("voivodeship.lubuskie")
        }
    ];

    const price = SUBSCRIPTION_PRICES[selectedVoivodeship][paymentPeriod];

    return (
        <div css={content}>
            <div css={header}>
                <Text variant="headline_4" as="p">
                    {t("pricing.bd_subscription.selected_city")}
                </Text>
                <Select
                    css={select}
                    instanceId="pricing_header_city_select"
                    onChange={(val) => {
                        if (val) {
                            setSelectedVoivodeship(val as SUBSCRIPTION_VOIVODESHIPS);
                        }
                    }}
                    options={options}
                    value={selectedVoivodeship}
                />
            </div>

            <div css={priceSection}>
                <Price price={price} primaryButton={{text: t("pricing.order"), href: "#contact"}} />
            </div>
        </div>
    );
};

const header = (theme: Theme) => css`
    ${p(4, 2, 4, 2)};
    ${backgroundColor(theme.colors.gray["100"])};
    ${borderRadius(2, 2, 0, 0)};
    ${flex("center", "space-between")};
    min-height: 62px;
    ${onDesktop(css`
        min-height: 112px;
    `)}
    flex-wrap: wrap;
    ${flexGap(1)};
`;

const priceSection = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    ${flexJustifyCenter};
`;

const content = css`
    ${borderRadius(2)};
    ${elevation(2)};
    background: #fff;
    ${h100};
    display: flex;
    flex-direction: column;
`;

const select = css`
    ${order(2)};
    max-width: 100%;
    min-width: 50%;

    ${onDesktop(css`
        min-width: 35%;
        max-width: 50%;
        ${order(3)}
    `)};
`;
